// console/src/components/SkillCatalog.tsx
import { useState, useEffect, useCallback, useRef } from 'react';
import { useApiHeaders } from '../hooks/useApiHeaders';
import { API_URL } from '../services/api';

interface SkillEntry {
	slug: string;
	name: string;
	description: string;
	github_url: string;
	version: string | null;
	downloads: number;
	installs: number;
	stars: number;
	updated_at: number;
}

interface SkillCategory {
	name: string;
	count: number;
	skills: SkillEntry[];
}

interface SkillCatalog {
	updated_at: string | null;
	total_skills: number;
	categories: SkillCategory[];
	source?: string;
}

interface SkillCatalogProps {
	onAddSkill: (skill: { skill_slug: string; skill_name: string; github_url: string; source: 'catalog' }) => void;
	installedSlugs: Set<string>;
	onClose: () => void;
}

function formatNumber(n: number): string {
	if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
	return String(n);
}

export default function SkillCatalogBrowser({ onAddSkill, installedSlugs, onClose }: SkillCatalogProps) {
	const { getHeaders } = useApiHeaders();
	const [catalog, setCatalog] = useState<SkillCatalog | null>(null);
	const [searchResults, setSearchResults] = useState<SkillEntry[] | null>(null);
	const [loading, setLoading] = useState(true);
	const [searching, setSearching] = useState(false);
	const [search, setSearch] = useState('');
	const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
	const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const headers = await getHeaders();
				const resp = await fetch(`${API_URL}/api/v1/skills/catalog`, { headers });
				if (!resp.ok) throw new Error('Failed to fetch catalog');
				const data = await resp.json();
				if (!cancelled) setCatalog(data);
			} catch {
				if (!cancelled) setCatalog({ updated_at: null, total_skills: 0, categories: [] });
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => { cancelled = true; };
	}, [getHeaders]);

	const doSearch = useCallback(async (query: string) => {
		if (!query.trim()) {
			setSearchResults(null);
			setSearching(false);
			return;
		}
		setSearching(true);
		try {
			const headers = await getHeaders();
			const resp = await fetch(`${API_URL}/api/v1/skills/search?q=${encodeURIComponent(query)}&limit=30`, { headers });
			if (resp.ok) {
				const data = await resp.json();
				setSearchResults(data.results || []);
			}
		} catch {
			// Fall back to local filtering
			setSearchResults(null);
		} finally {
			setSearching(false);
		}
	}, [getHeaders]);

	const handleSearchChange = (value: string) => {
		setSearch(value);
		if (searchTimer.current) clearTimeout(searchTimer.current);
		if (value.trim().length >= 2) {
			searchTimer.current = setTimeout(() => doSearch(value), 400);
		} else {
			setSearchResults(null);
		}
	};

	const toggleCategory = (name: string) => {
		setExpandedCategories(prev => {
			const next = new Set(prev);
			if (next.has(name)) next.delete(name);
			else next.add(name);
			return next;
		});
	};

	// Local filter fallback when search API not used
	const filteredCategories = (!searchResults && search.trim())
		? (catalog?.categories
			.map(cat => ({
				...cat,
				skills: cat.skills.filter(s =>
					s.name.toLowerCase().includes(search.toLowerCase()) ||
					s.description.toLowerCase().includes(search.toLowerCase()) ||
					s.slug.toLowerCase().includes(search.toLowerCase())
				),
			}))
			.filter(cat => cat.skills.length > 0) || [])
		: catalog?.categories || [];

	const renderSkillRow = (skill: SkillEntry) => {
		const isInstalled = installedSlugs.has(skill.slug);
		return (
			<div key={skill.slug} className="flex items-center justify-between px-4 py-3">
				<div className="flex-1 min-w-0 mr-4">
					<div className="flex items-center gap-2">
						<span className="font-medium text-sm text-slate-900 dark:text-white">{skill.name}</span>
						{skill.version && (
							<span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 font-mono">
								v{skill.version}
							</span>
						)}
					</div>
					<div className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">{skill.description}</div>
					{(skill.downloads > 0 || skill.stars > 0 || skill.installs > 0) && (
						<div className="flex items-center gap-3 mt-1">
							{skill.downloads > 0 && (
								<span className="text-[10px] text-slate-400 dark:text-slate-500 flex items-center gap-0.5">
									<svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
									{formatNumber(skill.downloads)}
								</span>
							)}
							{skill.installs > 0 && (
								<span className="text-[10px] text-slate-400 dark:text-slate-500 flex items-center gap-0.5">
									<svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" /></svg>
									{formatNumber(skill.installs)}
								</span>
							)}
							{skill.stars > 0 && (
								<span className="text-[10px] text-slate-400 dark:text-slate-500 flex items-center gap-0.5">
									<svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
									{skill.stars}
								</span>
							)}
						</div>
					)}
				</div>
				{isInstalled ? (
					<span className="text-xs text-green-600 dark:text-green-400 font-medium px-3 py-1 bg-green-50 dark:bg-green-900/20 rounded-full">
						Added
					</span>
				) : (
					<button
						onClick={() => onAddSkill({
							skill_slug: skill.slug,
							skill_name: skill.name,
							github_url: skill.github_url,
							source: 'catalog',
						})}
						className="text-xs font-medium px-3 py-1 bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 rounded-full hover:bg-primary-100 dark:hover:bg-primary-900/40 cursor-pointer"
					>
						+ Add
					</button>
				)}
			</div>
		);
	};

	return (
		<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
			<div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
				{/* Header */}
				<div className="p-6 border-b border-slate-200 dark:border-slate-800">
					<div className="flex items-center justify-between mb-4">
						<div>
							<div className="flex items-center gap-2">
								<h2 className="text-xl font-bold text-slate-900 dark:text-white">Skill Catalog</h2>
								<a
									href="https://clawhub.ai"
									target="_blank"
									rel="noopener noreferrer"
									className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:text-primary-500 transition-colors"
								>
									clawhub.ai
								</a>
							</div>
							<p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
								{catalog?.total_skills || 0} skills available
							</p>
						</div>
						<button
							onClick={onClose}
							className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
						>
							<svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
							</svg>
						</button>
					</div>
					<div className="relative">
						<input
							type="text"
							placeholder="Search skills on ClawHub..."
							value={search}
							onChange={e => handleSearchChange(e.target.value)}
							className="w-full px-4 py-2 pl-10 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
						/>
						<svg className="w-4 h-4 text-slate-400 absolute left-3 top-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
						</svg>
						{searching && (
							<div className="absolute right-3 top-3">
								<svg className="w-4 h-4 animate-spin text-slate-400" fill="none" viewBox="0 0 24 24">
									<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
									<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
								</svg>
							</div>
						)}
					</div>
				</div>

				{/* Catalog content */}
				<div className="flex-1 overflow-y-auto p-6 space-y-2">
					{loading ? (
						<div className="text-center py-8 text-slate-500">Loading catalog...</div>
					) : searchResults !== null ? (
						/* Search results */
						searchResults.length === 0 ? (
							<div className="text-center py-8 text-slate-500">No skills match your search</div>
						) : (
							<div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden divide-y divide-slate-100 dark:divide-slate-800">
								{searchResults.map(renderSkillRow)}
							</div>
						)
					) : filteredCategories.length === 0 ? (
						<div className="text-center py-8 text-slate-500">
							{search ? 'No skills match your search' : 'Catalog is empty — it may still be loading'}
						</div>
					) : (
						filteredCategories.map(cat => (
							<div key={cat.name} className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
								<button
									onClick={() => toggleCategory(cat.name)}
									className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer"
								>
									<span className="font-medium text-slate-900 dark:text-white">
										{cat.name}{' '}
										<span className="text-slate-400 font-normal">({cat.skills.length})</span>
									</span>
									<svg
										className={`w-5 h-5 text-slate-400 transition-transform ${expandedCategories.has(cat.name) ? 'rotate-180' : ''}`}
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
									</svg>
								</button>
								{expandedCategories.has(cat.name) && (
									<div className="border-t border-slate-200 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800">
										{cat.skills.map(renderSkillRow)}
									</div>
								)}
							</div>
						))
					)}
				</div>

				{/* Footer */}
				<div className="px-6 py-3 border-t border-slate-200 dark:border-slate-800 text-center">
					<a
						href="https://clawhub.ai"
						target="_blank"
						rel="noopener noreferrer"
						className="text-xs text-slate-400 hover:text-primary-500 transition-colors"
					>
						Browse more skills on clawhub.ai
					</a>
				</div>
			</div>
		</div>
	);
}
